/**
 * ACTIVITY BOX
 *
 * Renders technical output (code blocks, bash commands) in a clean box.
 * Plain text and status messages are routed to MessageBubble by AIConversation.
 * Uses Colony design tokens for consistent theming.
 */

import { type ChatMessage, type ActivityType } from "@/stores/reviewStore";
import {
  CheckCircle2,
  Loader2,
  AlertCircle,
  Copy,
  Check,
  Terminal,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";

const COPY_FEEDBACK_MS = 2000;

// Small inline status icon for the code block header
function StatusIcon({ type }: { type: ActivityType }) {
  if (type === "success") return <CheckCircle2 className="w-3 h-3" style={{ color: "var(--colony-green)" }} />;
  if (type === "progress") return <Loader2 className="w-3 h-3 animate-spin" style={{ color: "var(--colony-brand)" }} />;
  if (type === "error") return <AlertCircle className="w-3 h-3" style={{ color: "var(--colony-red)" }} />;
  return <Terminal className="w-3 h-3" style={{ color: "var(--colony-text-tertiary)" }} />;
}

interface ActivityBoxProps {
  message: ChatMessage;
}

function normalizeActivityLabel(raw?: string): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const normalized = trimmed.toLowerCase();
  if (
    normalized === "event" ||
    normalized === "events" ||
    normalized === "output" ||
    normalized === "activity" ||
    normalized === "worker-progress" ||
    normalized === "worker_log" ||
    normalized === "worker-log"
  ) {
    return null;
  }

  if (normalized === "claude_code") return "Claude Code";
  if (normalized === "codex" || normalized === "codex_review") return "Codex";

  return trimmed;
}

export function ActivityBox({ message }: ActivityBoxProps) {
  const [copied, setCopied] = useState<number | null>(null);
  const activityType = message.activityMeta?.activityType ?? "info";
  const variant = message.activityMeta?.variant;
  const codeBlocks = message.codeBlocks ?? [];
  const nodeLabel = normalizeActivityLabel(message.activityMeta?.nodeLabel || message.activityMeta?.nodeId);
  const title = message.activityMeta?.title;

  // Build a descriptive header label
  const headerLabel = (() => {
    if (variant === "command") return "Command";
    if (variant === "code") return "Code";
    if (nodeLabel) return nodeLabel;
    if (title) return title;
    return "Output";
  })();

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleCopy = async (code: string, index: number) => {
    await navigator.clipboard.writeText(code);
    setCopied(index);
    setTimeout(() => setCopied(null), COPY_FEEDBACK_MS);
  };

  if (codeBlocks.length === 0) return null;

  return (
    <div className="py-1.5">
      <div className="space-y-2">
        {codeBlocks.map((block, index) => (
          <div
            key={`${message.id}-code-${index}`}
            className="rounded-[8px] overflow-hidden border border-[var(--colony-border)]"
            style={{ backgroundColor: "var(--colony-surface)" }}
          >
            <div
              className="flex items-center justify-between px-3 py-1.5 border-b border-[var(--colony-border)]"
              style={{ backgroundColor: "var(--colony-surface-elevated)" }}
            >
              <div className="flex items-center gap-2 text-[11px]" style={{ color: "var(--colony-text-tertiary)" }}>
                <StatusIcon type={activityType} />
                <span className="font-medium" style={{ color: "var(--colony-text-secondary)" }}>
                  {block.filename || headerLabel}
                </span>
                {block.language && block.filename && (
                  <>
                    <span style={{ color: "var(--colony-text-tertiary)" }}>·</span>
                    <span>{block.language}</span>
                  </>
                )}
                {!block.filename && block.language && block.language !== "text" && (
                  <>
                    <span style={{ color: "var(--colony-text-tertiary)" }}>·</span>
                    <span>{block.language}</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px]" style={{ color: "var(--colony-text-tertiary)" }}>
                  {formatTime(message.timestamp)}
                </span>
                <button
                  onClick={() => handleCopy(block.code, index)}
                  className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded transition-colors hover:bg-[var(--colony-surface-hover)]"
                  style={{ color: "var(--colony-text-tertiary)" }}
                >
                  {copied === index ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copied === index ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
            <pre
              className="px-3 py-2.5 text-[11px] leading-relaxed overflow-x-auto overflow-y-auto whitespace-pre-wrap break-words font-mono max-h-[240px]"
              style={{ color: "var(--colony-text-secondary)" }}
            >
              {block.code}
            </pre>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Grouped Activity Box ─────────────────────────────────────────────

interface GroupedActivityBoxProps {
  messages: ChatMessage[];
}

export function GroupedActivityBox({ messages }: GroupedActivityBoxProps) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const preRef = useRef<HTMLPreElement>(null);
  const [combinedText, setCombinedText] = useState("");

  const lastMessage = messages[messages.length - 1];
  const lastActivityType = lastMessage?.activityMeta?.activityType ?? "info";

  // Force re-render when the streaming timeout expires so "Running..." disappears
  const [, setTick] = useState(0);
  const lastTs = new Date(lastMessage?.timestamp ?? 0).getTime();
  const age = Date.now() - lastTs;
  const isStreaming = lastActivityType === "progress" && age < 15_000;

  useEffect(() => {
    if (!isStreaming) return;
    const remaining = 15_000 - age + 100; // 100ms buffer
    if (remaining <= 0) return;
    const timer = setTimeout(() => setTick((t) => t + 1), remaining);
    return () => clearTimeout(timer);
  }, [lastTs, isStreaming, age]);

  // Auto-scroll to bottom when new lines arrive (only when streaming)
  useEffect(() => {
    if (preRef.current && isStreaming) {
      preRef.current.scrollTop = preRef.current.scrollHeight;
    }
  }, [messages.length, combinedText, isStreaming]);

  // Determine header label from first message's nodeLabel, or "Terminal"
  const headerLabel = (() => {
    for (const msg of messages) {
      const label = normalizeActivityLabel(msg.activityMeta?.nodeLabel || msg.activityMeta?.nodeId);
      if (label) {
        return label;
      }
    }
    return "Terminal";
  })();

  // Time range
  const formatTime = (timestamp: string) =>
    new Date(timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const firstTime = formatTime(messages[0].timestamp);
  const lastTime = formatTime(lastMessage.timestamp);
  const timeLabel = firstTime === lastTime ? firstTime : `${firstTime} – ${lastTime}`;

  // Combine all lines: use codeBlock content if present, otherwise title/content
  // Deduplicate consecutive identical lines
  useEffect(() => {
    const lines: string[] = [];
    let lastLine = "";

    for (const msg of messages) {
      let line: string;
      if (msg.codeBlocks?.length) {
        line = msg.codeBlocks.map((b) => b.code).join("\n");
      } else {
        line = msg.activityMeta?.title || msg.content || "";
      }

      // Skip if same as previous line (dedup consecutive duplicates)
      if (line && line !== lastLine) {
        lines.push(line);
        lastLine = line;
      }
    }

    setCombinedText(lines.join("\n"));
  }, [messages]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(combinedText);
    setCopied(true);
    setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
  };

  if (!combinedText.trim()) return null;

  const lineCount = combinedText.split("\n").length;
  const maxHeight = expanded ? "max-h-[480px]" : "max-h-[180px]";
  const showExpandButton = lineCount > 8 && !expanded;

  return (
    <div className="py-1.5">
      <div
        className={cn(
          "rounded-[8px] overflow-hidden border transition-all duration-200",
          isStreaming
            ? "border-[var(--colony-brand)]"
            : "border-[var(--colony-border)]"
        )}
        style={{ backgroundColor: "var(--colony-surface)" }}
      >
        <div
          className="flex items-center justify-between px-3 py-1.5 border-b border-[var(--colony-border)]"
          style={{ backgroundColor: "var(--colony-surface-elevated)" }}
        >
          <div className="flex items-center gap-2 text-[11px]" style={{ color: "var(--colony-text-tertiary)" }}>
            <StatusIcon type={isStreaming ? "progress" : lastActivityType} />
            <span className="font-medium" style={{ color: "var(--colony-text-secondary)" }}>{headerLabel}</span>
            <span style={{ color: "var(--colony-text-tertiary)" }}>·</span>
            <span>{lineCount} {lineCount === 1 ? "line" : "lines"}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px]" style={{ color: "var(--colony-text-tertiary)" }}>{timeLabel}</span>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded transition-colors hover:bg-[var(--colony-surface-hover)]"
              style={{ color: "var(--colony-text-tertiary)" }}
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
        <div className="relative">
          <pre
            ref={preRef}
            className={cn(
              "px-3 py-2.5 text-[11px] leading-relaxed overflow-x-auto overflow-y-auto whitespace-pre-wrap break-words font-mono transition-all duration-200",
              maxHeight
            )}
            style={{ color: "var(--colony-text-secondary)" }}
          >
            {combinedText}
            {isStreaming && (
              <span className="inline-block ml-1 animate-pulse" style={{ color: "var(--colony-brand)", opacity: 0.8 }}>▋</span>
            )}
          </pre>
          {showExpandButton && (
            <div
              className="absolute bottom-0 left-0 right-0 h-12 flex items-end justify-center pb-2"
              style={{ background: "linear-gradient(to top, var(--colony-surface), transparent)" }}
            >
              <button
                onClick={() => setExpanded(true)}
                className="text-[10px] px-3 py-1 rounded transition-colors"
                style={{
                  color: "var(--colony-text-tertiary)",
                  backgroundColor: "var(--colony-surface-elevated)",
                }}
              >
                Show more ({lineCount - 8} more lines)
              </button>
            </div>
          )}
        </div>
        {isStreaming && (
          <div
            className="px-3 py-1.5 flex items-center gap-2 border-t border-[var(--colony-border)]"
            style={{ backgroundColor: "var(--colony-surface-elevated)" }}
          >
            <div className="flex gap-0.5">
              <div className="w-1 h-1 rounded-full animate-pulse" style={{ backgroundColor: "var(--colony-brand)", opacity: 0.8 }} />
              <div className="w-1 h-1 rounded-full animate-pulse [animation-delay:200ms]" style={{ backgroundColor: "var(--colony-brand)", opacity: 0.8 }} />
              <div className="w-1 h-1 rounded-full animate-pulse [animation-delay:400ms]" style={{ backgroundColor: "var(--colony-brand)", opacity: 0.8 }} />
            </div>
            <span className="text-[10px]" style={{ color: "var(--colony-brand)", opacity: 0.7 }}>Running...</span>
          </div>
        )}
      </div>
    </div>
  );
}
